import { DOCUMENT } from '@angular/common';
import {
  DestroyRef,
  Injectable,
  effect,
  inject,
  untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '@app/features/auth/auth.selectors';
import { ANONYMOUS_SESSION } from '@core/services/auth/access-claims';
import { APPLICATION_SESSION } from '@core/services/auth/application-session';
import { NotificationFeedService } from './notification-feed.service';

const OPEN_REQUEST = 'mt-notification.open';
const OPEN_ACCEPTED = 'mt-notification.open.accepted';
const NOTIFICATION_PARAMETER = 'mtNotification';
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface NotificationOpenRequest {
  readonly type: typeof OPEN_REQUEST;
  readonly schemaVersion: 1;
  readonly notificationId: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationPushNavigationService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly feed = inject(NotificationFeedService);
  private readonly store = inject(Store);
  private readonly currentUser = this.store.selectSignal(selectCurrentUser);
  private readonly sessionState = toSignal(inject(APPLICATION_SESSION).state$, {
    initialValue: ANONYMOUS_SESSION,
  });
  private pendingNotificationId: string | null = null;
  private started = false;

  public constructor() {
    effect(() => {
      this.currentUser();
      this.sessionState();
      untracked(() => this.openPendingNotification());
    });
  }

  public start(): void {
    if (this.started) {
      return;
    }
    this.started = true;

    const window = this.document.defaultView;
    window?.navigator.serviceWorker?.addEventListener(
      'message',
      this.handleWorkerMessage,
    );
    this.destroyRef.onDestroy(() =>
      window?.navigator.serviceWorker?.removeEventListener(
        'message',
        this.handleWorkerMessage,
      ),
    );

    if (!window) {
      return;
    }
    const url = new URL(window.location.href);
    const notificationId = url.searchParams.get(NOTIFICATION_PARAMETER);
    if (!isNotificationId(notificationId)) {
      return;
    }

    url.searchParams.delete(NOTIFICATION_PARAMETER);
    window.history.replaceState(window.history.state, '', url.href);
    this.queue(notificationId);
  }

  private readonly handleWorkerMessage = (
    event: MessageEvent<unknown>,
  ): void => {
    if (!isOpenRequest(event.data)) {
      return;
    }

    event.ports[0]?.postMessage({
      type: OPEN_ACCEPTED,
      schemaVersion: 1,
    });
    this.queue(event.data.notificationId);
  };

  private queue(notificationId: string): void {
    this.pendingNotificationId = notificationId;
    this.openPendingNotification();
  }

  private openPendingNotification(): void {
    const notificationId = this.pendingNotificationId;
    if (
      !notificationId ||
      !this.currentUser() ||
      !this.sessionState().authenticated
    ) {
      return;
    }

    this.pendingNotificationId = null;
    this.feed.openById(notificationId);
  }
}

export function notificationPushNavigationInitializer(): void {
  inject(NotificationPushNavigationService).start();
}

function isOpenRequest(value: unknown): value is NotificationOpenRequest {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const request = value as Partial<NotificationOpenRequest>;
  return (
    request.type === OPEN_REQUEST &&
    request.schemaVersion === 1 &&
    isNotificationId(request.notificationId)
  );
}

function isNotificationId(value: unknown): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value);
}
